import React, { useEffect, useState } from "react";
import { fetchFolderFiles } from "../../api/google";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  HStack,
  Image,
  Input,
  List,
  ListIcon,
  ListItem,
  Skeleton,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { ArrowLeft, Upload } from "react-feather";
import {
  fetchCustomerFileRequests,
  updateFileRequest,
} from "../../api/fileRequests";
import { GenericModal } from "./modal";
import { Context } from "../../ContextWrapper";

export const GoogleDriveView = ({ customer }) => {
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (customer?.google_drive_folder_id) {
      let homeItem = {
        id: customer.google_drive_folder_id,
        name: "Home",
      };
      setPath([homeItem]);
    }
  }, [customer]);

  useEffect(() => {
    if (path.length > 0) {
      const fetchData = async () => {
        const folderData = await fetchFolderFiles(path.at(-1).id);
        setFiles(folderData.results);
        setLoading(false);
      };
      fetchData();
    }
  }, [path]);

  const handleClick = (item) => {
    if (item.mimeType === "application/vnd.google-apps.folder") {
      setLoading(true);
      setPath((prev) => [...prev, item]);
    } else {
      window.open(item.webViewLink, "_blank");
    }
  };

  const handleBack = () => {
    setLoading(true);
    setPath(path.slice(0, -1));
  };

  const handleBreadcrumbClick = (event, item) => {
    event.preventDefault();
    setLoading(true);
    setPath(path.slice(0, path.indexOf(item) + 1));
  };

  return (
    <List spacing={3}>
      <FolderBreadCrumb path={path} handleClick={handleBreadcrumbClick} />
      {loading && (
        <>
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
          <Skeleton height="20px" />
        </>
      )}

      {!loading && path.at(-1).id !== customer?.google_drive_folder_id && (
        <ListItem onClick={handleBack}>
          <ListIcon color="grey.500">
            <ArrowLeft />
          </ListIcon>
          Back
        </ListItem>
      )}
      {!loading &&
        files?.map((item) => (
          <DriveItem key={item.id} item={item} onClick={handleClick} />
        ))}
      <FileRequestItem customer_id={customer?.id} />
    </List>
  );
};

const FolderBreadCrumb = ({ path, handleClick }) => {
  return (
    <Breadcrumb>
      {path?.map((item) => (
        <BreadcrumbItem
          key={item.id}
          isCurrentPage={item.id === path.at(-1).id}
          onClick={(event) =>
            item.id !== path.at(-1).id && handleClick(event, item)
          }
        >
          <BreadcrumbLink href="#">{item.name}</BreadcrumbLink>
        </BreadcrumbItem>
      ))}
    </Breadcrumb>
  );
};

const DriveItem = ({ item, onClick }) => {
  return (
    <ListItem onClick={() => onClick(item)}>
      <HStack>
        <Image src={item.iconLink} />
        <Box>{item.name}</Box>
      </HStack>
    </ListItem>
  );
};

const FileRequestItem = ({ customer_id }) => {
  const [fileRequests, setFileRequests] = useState([]);
  const context = React.useContext(Context);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const fetchData = async () => {
      const fileRequestsResult = await fetchCustomerFileRequests(customer_id, {
        status: "pending",
      });
      setFileRequests(fileRequestsResult);
    };
    fetchData();
  }, [customer_id]);

  const onUploadFile = (e, fileRequest) => {
    fileRequest.file = e.target.files[0];
    fileRequests.map((o) => (o.id === fileRequest.id ? fileRequest : o));
  };

  const handleUpload = (fileRequest) => {
    console.log("Uploading file:", fileRequest);

    if (!fileRequest.file) {
      context.fireToast("Error", "Please select a file to upload", "error");
      return;
    }

    setFileRequests((prevList) =>
      prevList.map((item) =>
        item.id === fileRequest.id ? { ...item, loading: true } : item
      )
    );

    updateFileRequest(customer_id, fileRequest.id, fileRequest)
      .then((res) => {
        setFileRequests((prevList) =>
          prevList.filter((item) => item.id !== fileRequest.id)
        );
        context.fireToast("Success", "Account has been created", "success");
      })
      .catch((err) => {
        setFileRequests((prevList) =>
          prevList.map((item) =>
            item.id === fileRequest.id ? { ...item, loading: false } : item
          )
        );
        context.fireToast("Error", "Failed to upload file", "error");
        console.error("Error uploading file:", err);
      });
  };

  return (
    fileRequests?.length > 0 && (
      <>
        <GenericModal
          isOpen={isOpen}
          onClose={onClose}
          title={`Upload files requested`}
          body={
            <List spacing={3}>
              {fileRequests?.map((item) => (
                <Tooltip label={item.description} key={item.id}>
                  <ListItem>
                    <Text>{item.name}</Text>
                    <HStack mb={5}>
                      <Input
                        type="file"
                        sx={{
                          "::file-selector-button": {
                            height: 10,
                            padding: 0,
                            mr: 4,
                            background: "none",
                            border: "none",
                            fontWeight: "bold",
                          },
                        }}
                        onChange={(event) => onUploadFile(event, item)}
                      />
                      <Button
                        isDisabled={item.loading}
                        onClick={() => handleUpload(item)}
                        mr={3}
                      >
                        {item.loading ? <Spinner size="sm" /> : "Upload"}
                      </Button>
                    </HStack>
                  </ListItem>
                </Tooltip>
              ))}
            </List>
          }
        ></GenericModal>
        <Button onClick={onOpen} leftIcon={<Upload boxSize={3} />}>
          File Requests ({fileRequests.length})
        </Button>
      </>
    )
  );
};
